import {ZoomCommand} from '../../enums/ZoomCommand';
import {IPoint} from '../../interfaces/primitives/IPoint';
import PixiUtil from '../../../api/render/pixi/PixiUtil';
import {DynamicData} from '../../data/DynamicData';
import {DraggableObject} from '../../base/DraggableObject';
import {TableBackground} from './game_table/TableBackground';
import {RoomTableMouseWheelController} from '../../controllers/silent/RoomTableMouseWheelController';
import {TableObjects} from './game_table/TableObjects';
import {InteractionEvent, Point} from 'pixi.js';
import {StackSelector} from '../../controllers/StackSelector';
import {ActiveData} from '../../data/ActiveData';
import {GameTableEvents} from './game_table/GameTableEvents';
import EventApi from '../../../../../api/EventsApi';
import {PixiApplicationController} from '../../controllers/PixiApplicationController';
import {SelectorRectangle} from './game_table/SelectorRectangle';
import {IRectangle} from '../../interfaces/primitives/IRectangle';
import {CardSelector} from '../../controllers/CardSelector';
import {UnselectByMouseStoppedController} from '../../controllers/silent/UnselectByMouseStoppedController';
import {HandEvents} from './hand/HandEvents';

export class GameTable extends DraggableObject
{
    tableObjects: TableObjects | undefined;
    readonly background: TableBackground;
    private readonly buttonScale: number = 1.15;
    private readonly wheelScale: number = 1.03;
    private readonly maxScale: number = 1.5;
    private onZoomBindThis: (e: Event) => void;

    private minScale: number = 1;
    private selectorRectangle: SelectorRectangle;

    constructor() {
        super();

        this.background = new TableBackground();
        this.selectorRectangle = new SelectorRectangle();

        this.addChild(this.background);
        this.addChild(this.selectorRectangle);

        this.resize();

        RoomTableMouseWheelController.start();
        UnselectByMouseStoppedController.start();

        this.onZoomBindThis = this.onZoom.bind(this);
        addEventListener(GameTableEvents.ZOOM, this.onZoomBindThis);

        this.selectorRectangle.interactive = this.selectorRectangle.interactiveChildren = false;
    }

    /**
     * Обработчик зум-комманды
     */
    onZoom(e: Event): void {
        let data: {command: ZoomCommand, wheelOffset: IPoint} = (e as MessageEvent).data;
        this.zoom(data.command, data.wheelOffset);
    }

    /**
     * Применить зум
     * @param scale - масштаб
     * @param wheel - точка расположения указателя в координатах игрового поля
     */
    public setZoom(scale: number, wheel?: IPoint): void {
        scale = this.clampScale(scale);
        const oldScale = this.scale.x;
        this.scale.set(scale);
        this.selectorRectangle!.setScale(scale);

        let pointX = wheel ? wheel.x : (DynamicData.sceneSizes.width / 2);
        let pointY = wheel ? wheel.y : (DynamicData.sceneSizes.height / 2);

        let worldPos = {
            x: (pointX - this.x) / oldScale,
            y: (pointY - this.y) / oldScale
        };
        const newX = pointX - worldPos.x * scale;
        const newY = pointY - worldPos.y * scale;
        this.moveTo(this.clampPos(newX, newY));
    }

    /**
     * Применяем позиционирование
     * @param position - объект содержащий координаты применяемой позиции по осям
     */
    moveTo(position: IPoint): void {
        super.moveTo(position);
        dispatchEvent(new MessageEvent(GameTableEvents.POSITION_AND_SCALE, {data: null}));
    }

    /**
     * Восстановить зум
     */
    public resetZoom(): void {

        const origScale = ActiveData.root.stage.scale.x;
        const kResize = Math.min(DynamicData.sceneSizes.width / ActiveData.root.stage.width, DynamicData.sceneSizes.height / ActiveData.root.stage.height);
        const scale = origScale * kResize;
        this.setZoom(scale);

        const fieldWidth = ActiveData.root.width * scale, fieldHeight = ActiveData.root.height * scale;

        let newX: number, newY: number;
        if (fieldWidth < DynamicData.sceneSizes.width && fieldHeight < DynamicData.sceneSizes.height) {
            newX = (DynamicData.sceneSizes.width - fieldWidth) / 2;
            newY = (DynamicData.sceneSizes.height - fieldHeight) / 2;
        } else {
            const pos = {
                x: (ActiveData.root.stage.x - ActiveData.root.stage.width / 2) / origScale,
                y: (ActiveData.root.stage.y - ActiveData.root.stage.height / 2) / origScale
            };
            newX = pos.x * scale + DynamicData.sceneSizes.width / 2;
            newY = pos.y * scale + DynamicData.sceneSizes.height / 2;
        }

        this.moveTo(this.clampPos(newX, newY));
    }

    /**
     * Ограничение перемещения внутри области перетаскивания
     * @param x - желаемая координата по горизонтали
     * @param y - желаемая координата по вертикали
     * @returns объект содержащий возможные координаты, полученные из желаемых
     */
    clampPos(x: number, y: number): IPoint {
        let scale: number = this.scale.x;
        x = PixiUtil.clamp(DynamicData.sceneSizes.width - ActiveData.root.width * scale, x, 0);
        y = PixiUtil.clamp(DynamicData.sceneSizes.height - ActiveData.root.height * scale, y, 0);
        return {x, y};
    }

    /**
     * Загрузка и установка фона и объектов игрового поля
     */
    async initialize(): Promise<void> {
        await this.background.initialize();
        if (this.tableObjects) {
            this.removeChild(this.tableObjects);
            this.tableObjects.destroy();
        }
        this.tableObjects = new TableObjects();
        this.addChild(this.tableObjects);
        await this.tableObjects.initialize();

        this.setChildIndex(this.selectorRectangle, this.children.length - 1);
    }

    /**
     * Перерисовка при изменении размеров окна
     */
    resize() {
        this.updateMinScale();
        this.resetZoom();
    }

    /**
     * Минимальный зум
     */
    public minZoom(): void {
        this.setZoom(this.minScale);
        let newX = (DynamicData.sceneSizes.width - ActiveData.root.width * this.scale.x) / 2;
        let newY = (DynamicData.sceneSizes.height - ActiveData.root.height * this.scale.y) / 2;
        this.moveTo(this.clampPos(newX, newY));
    }

    onPointerUp(e: InteractionEvent): void {
        if (!this.dragged) {
            EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'click'});
            StackSelector.unselectAll();
            CardSelector.unselectAll();
            dispatchEvent(new MessageEvent(HandEvents.SORT_CARDS, {data: {card: null}}));
        }
        super.onPointerUp(e);
    }

    onPointerDown(e: InteractionEvent): void {
        if (e.data.originalEvent.shiftKey) {
            this.startDrawSelectingRect(e);
        } else {
            super.onPointerDown(e);
            DynamicData.dragSomething = false;
        }
    }

    destroy(): void {
        removeEventListener(GameTableEvents.ZOOM, this.onZoomBindThis);
        delete this.onZoomBindThis;

        super.destroy();
    }

    zoom(zoomCommand: ZoomCommand, wheelOffset?: IPoint) {
        let scaleMultiplier: number = this.wheelScale ? this.wheelScale : this.buttonScale;
        let scale = this.scale.x;
        switch (zoomCommand) {
            case ZoomCommand.ZOOM_RESET:
                this.resetZoom();
                return;
            case ZoomCommand.ZOOM_MIN:
                this.minZoom();
                return;
            case ZoomCommand.ZOOM_IN:
                scale *= scaleMultiplier;
                break;
            case ZoomCommand.ZOOM_OUT:
                scale /= scaleMultiplier;
                break;
        }

        this.setZoom(scale, wheelOffset);
    }

    protected onSelectorRectMove(e: InteractionEvent): void {
        this.movePosition = this.parent.toLocal(e.data.global, void 0);
        this.movePosition.x -= this.x;
        this.movePosition.y -= this.y;
        let resultRect: IRectangle = this.selectorRectangle.drawTo(this.movePosition);
        StackSelector.selectStacksInRect(this.tableObjects!.stacks.getStacks(), resultRect);
    }

    private startDrawSelectingRect(e: InteractionEvent): void {
        StackSelector.unselectAll();
        let position: Point = this.parent.toLocal(e.data.global);
        position.x -= this.x;
        position.y -= this.y;
        this.selectorRectangle!.startAt(position);
        PixiApplicationController.app.stage.on('pointermove', this.onSelectorRectMove, this);
        PixiApplicationController.app.stage.on('pointerup', this.onSelectorRectUp, this);
        PixiApplicationController.app.stage.on('pointerupoutside', this.onSelectorRectUp, this);
    }

    private onSelectorRectUp(): void {
        this.selectorRectangle.end();
        PixiApplicationController.app.stage.off('pointermove', this.onSelectorRectMove, this);
        PixiApplicationController.app.stage.off('pointerup', this.onSelectorRectUp, this);
        PixiApplicationController.app.stage.off('pointerupoutside', this.onSelectorRectUp, this);
    }

    /**
     * Установка минимального зума
     */
    private updateMinScale(): void {
        this.minScale = Math.max(DynamicData.sceneSizes.width / ActiveData.root.width, DynamicData.sceneSizes.height / ActiveData.root.height);
    }

    /**
     * Ограничения масштаба
     */
    private clampScale(scale: number): number {
        return PixiUtil.clamp(this.minScale, scale, this.maxScale);
    }
}
